import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Subject } from 'rxjs';
import { MessageService, ConfirmationService } from 'primeng/api';

import { LivroCaixa, TipoLancamento } from '../../core/models/livro-caixa';
import { LivroCaixaService } from 'src/app/core/services/livro-caixa';
import { AuthService } from 'src/app/core/services/auth.service';
import { UsuarioService } from 'src/app/core/services/usuario.service';

@Component({
  selector: 'app-caixa',
  templateUrl: './livro-caixa.component.html',
  styleUrls: ['./livro-caixa.component.scss'],
})
export class LivroCaixaComponent implements OnInit, OnDestroy {

  selectedLancamento: string | null = 'TODOS';
  livroCaixa = new LivroCaixa();
  lancamentos: LivroCaixa[] = [];
  lancamentosOriginais: LivroCaixa[] = [];

  editMode = false;
  editId: number | null = null;

  dataInicial: string = '';
  dataFinal: string = '';

  totalValorItens: number = 0;

  tipos = Object.values(TipoLancamento);

    lancamentoOptions = [
      { label: 'Todas', value: 'TODOS' },
      { label: 'Entrada', value: 'E' },
      { label: 'Saída', value: 'S' },
    ];

  @ViewChild('livroCaixaForm') livroCaixaForm?: NgForm;

  private readonly destroy$ = new Subject<void>();

  usuarioIdToNome: { [key: number]: string } = {};

  constructor(
    private readonly service: LivroCaixaService,
    private readonly messageService: MessageService,
    private readonly confirmation: ConfirmationService,
    private readonly authService: AuthService,
    private readonly usuarioService: UsuarioService
  ) {}

    ngOnInit(): void {
    this.setDataInicialEFinal();
    this.carregarUsuarios();
    this.carregarLancamentos();
  }

  carregarUsuarios(): void {
    this.usuarioService.selecionar().subscribe(usuarios => {
      this.usuarioIdToNome = {};
      usuarios.forEach(u => {
        if (u.id) this.usuarioIdToNome[u.id] = u.username;
      });
    });
  }

  setDataInicialEFinal(): void {
    const hoje = new Date();
    this.dataInicial = this.formatarData(hoje);
    this.dataFinal = this.formatarData(hoje);
  }

  formatarData(data: Date): string {
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  }

  converterParaFormatoBR(data: string): string {
    const [ano, mes, dia] = data.split('-');
    return `${dia}/${mes}/${ano}`;
  }

  carregarLancamentos(): void {
    if (this.dataInicial && this.dataFinal) {
      const dataInicialBR = this.converterParaFormatoBR(this.dataInicial);
      const dataFinalBR = this.converterParaFormatoBR(this.dataFinal);
      this.service.selecionarPorData(dataInicialBR, dataFinalBR).subscribe((retorno) => {
        this.lancamentosOriginais = retorno;
        this.filtrarLancamentos();
      });
    } else {
      alert('Por favor, selecione as duas datas.');
    }
  }

  cadastrar(): void {
    // Preencher usuarioId automaticamente
    const currentUser = this.authService.getCurrentUser();
    if (currentUser?.id) {
      // Se o id já está no token, use diretamente
      this.livroCaixa.usuarioId = currentUser.id;
      this.service.lancar(this.livroCaixa).subscribe({
        next: (ret: LivroCaixa) => {
          this.livroCaixa = new LivroCaixa();
          this.carregarLancamentos();
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Lançamento cadastrado com sucesso.',
          });
        },
        error: (err: any) => {
          console.error('Erro ao cadastrar lançamento', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Erro ao cadastrar lançamento.',
          });
        },
      });
      return;
    }
    // Fallback: buscar o id do usuário pelo username
    const username = currentUser?.sub;
    if (!username) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Usuário logado não identificado.'
      });
      return;
    }
    this.usuarioService.selecionar().subscribe({
      next: (usuarios) => {
        const usuario = usuarios.find(u => u.username === username);
        if (!usuario || !usuario.id) {
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'ID do usuário não encontrado.'
          });
          return;
        }
        this.livroCaixa.usuarioId = usuario.id;
        this.service.lancar(this.livroCaixa).subscribe({
          next: (ret: LivroCaixa) => {
            this.livroCaixa = new LivroCaixa();
            this.carregarLancamentos();
            this.messageService.add({
              severity: 'success',
              summary: 'Sucesso',
              detail: 'Lançamento cadastrado com sucesso.',
            });
          },
          error: (err: any) => {
            console.error('Erro ao cadastrar lançamento', err);
            this.messageService.add({
              severity: 'error',
              summary: 'Erro',
              detail: 'Erro ao cadastrar lançamento.',
            });
          },
        });
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao buscar usuário logado.'
        });
      }
    });
  }

  onSubmit(): void {
  this.cadastrar();
  }

  editar(l: LivroCaixa): void {
    this.editMode = true;
    this.editId = l.id ?? null;
    this.livroCaixa = { ...l };
  }

  cancelarEdicao(): void {
    this.editMode = false;
    this.editId = null;
    this.livroCaixa = new LivroCaixa();

    try {
      this.livroCaixaForm?.resetForm(this.livroCaixa);
    } catch (e) {
      console.warn('Erro ao resetar o formulário:', e);
    }
  }

  limparFormulario(): void {
    this.livroCaixa = new LivroCaixa();
    this.editMode = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  filtrarLancamentos(): void {
    let filtrados = [...this.lancamentosOriginais];
    if (this.selectedLancamento && this.selectedLancamento !== 'TODOS') {
      filtrados = filtrados.filter(l => l.tipoLancamento === this.selectedLancamento);
    }
    // Padronizar tipoLancamento para o enum TipoLancamento
    this.lancamentos = filtrados.map(l => ({
      ...l,
      tipoLancamento: String(l.tipoLancamento) === 'E' ? TipoLancamento.E :
                      String(l.tipoLancamento) === 'S' ? TipoLancamento.S :
                      l.tipoLancamento
    }));
    this.calcularTotalValorItens();
  }

  calcularTotalValorItens(): void {
    console.log('Lancamentos filtrados:', this.lancamentos);
    this.totalValorItens = this.lancamentos.reduce((acc, l) => {
      console.log('tipoLancamento:', l.tipoLancamento, 'valor:', l.valor);
      if (l.tipoLancamento === TipoLancamento.E) {
        return acc + (l.valor || 0);
      } else if (l.tipoLancamento === TipoLancamento.S) {
        return acc - (l.valor || 0);
      }
      return acc;
    }, 0);
    console.log('Total calculado:', this.totalValorItens);
  }

  onDataChange(): void {
    this.carregarLancamentos();
  }

  onFiltroChange(): void {
    this.filtrarLancamentos();
  }

  getStatusLabel(status: string): string {
    const foundStatus = this.lancamentoOptions.find(s => s.value === status);
    return foundStatus ? foundStatus.label : status;
  }

  getTipoLancamentoLabel(tipo: string): string {
    if (tipo === 'E') return 'Entrada';
    if (tipo === 'S') return 'Saída';
    return tipo;
  }

  limparFiltros(): void {
    this.selectedLancamento = null;
    this.filtrarLancamentos();
  }

  getNomeUsuario(usuarioId: number): string {
    return this.usuarioIdToNome[usuarioId] || String(usuarioId);
  }

isEntrada(tipo: string): boolean {
  return tipo.startsWith('E')
}

}
